import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { Calendar, ChevronRight, LucideAngularModule, MapPin, Tags } from 'lucide-angular';
import { map, type Observable, of } from 'rxjs';
import type { EventCategory, EventType } from '../../../core/models/middleware/event-item.model';
import type { OverviewItemModel } from '../../../core/models/middleware/overview-item.model';
import { CurrentTimeService } from '../../../core/services/current-time.service';
import {
  getEventCategoryColor,
  getEventCategoryLabel,
  mapEventTypeToCategory,
} from '../../../core/utils/event-category.util';
import { BadgeComponent } from '../../../shared/components/badge/badge.component';
import { DatetimeInfoLineComponent } from '../../../shared/components/datetime-info-line/datetime-info-line.component';
import { KeywordsInfoLineComponent } from '../../../shared/components/keywords-info-line/keywords-info-line.component';
import { LiveBadgeComponent } from '../../../shared/components/live-badge/live-badge.component';
import { LocationInfoLineComponent } from '../../../shared/components/location-info-line/location-info-line.component';
import { SharedCardComponent } from '../../../shared/components/shared-card/shared-card.component';

@Component({
  selector: 'app-event-card',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    LiveBadgeComponent,
    SharedCardComponent,
    BadgeComponent,
    KeywordsInfoLineComponent,
    DatetimeInfoLineComponent,
    LocationInfoLineComponent,
  ],
  templateUrl: './event-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventCardComponent {
  title = input.required<string>();
  dateText = input.required<string>();
  eventId = input.required<string>();
  eventType = input.required<EventType>();
  eventCategory = input<EventCategory>();
  location = input<string>('');
  keywords = input<string[]>([]);
  event = input<OverviewItemModel>();
  showLiveBadge = input<boolean>(true);

  protected computedEventCategory = computed(() => {
    if (this.eventType() === 'Other') {
      if (!this.eventCategory()) {
        throw new Error('Event Category not specified');
      }
      return this.eventCategory()!;
    }
    return mapEventTypeToCategory(this.eventType());
  });

  // Lucide Icons
  protected readonly MapPin = MapPin;
  protected readonly Calendar = Calendar;
  protected readonly ChevronRight = ChevronRight;
  protected readonly Tags = Tags;

  private readonly router = inject(Router);
  private readonly currentTimeService = inject(CurrentTimeService);

  openEvent(documentId: string, eventType: EventType): void {
    this.router.navigate(['event', 'overview', eventType, documentId]).then();
  }

  getEventCategoryLabel(eventType: EventType, category?: EventCategory): string {
    return getEventCategoryLabel(eventType, category);
  }

  getEventCategoryColor(category: EventCategory): string {
    return getEventCategoryColor(category);
  }

  isEventCurrentlyLive(): Observable<boolean> {
    const eventData = this.event();
    if (!eventData) {
      return of(false);
    }

    return this.currentTimeService.getCurrentTime().pipe(
      map((currentTime) => {
        try {
          const now = currentTime.getTime();

          // Ensure dates are Date objects and valid
          const eventStart = new Date(eventData.startDate);
          const eventEnd = eventData.endDate ? new Date(eventData.endDate) : null;

          // Check if dates are valid
          if (Number.isNaN(eventStart.getTime())) {
            return false;
          }

          const eventStartTime = eventStart.getTime();
          const eventEndTime = eventEnd && !Number.isNaN(eventEnd.getTime()) ? eventEnd.getTime() : null;

          // If no end date, assume event lasts for 4 hours
          const actualEventEnd = eventEndTime || eventStartTime + 4 * 60 * 60 * 1000;

          return now >= eventStartTime && now <= actualEventEnd;
        } catch (error) {
          console.warn('Error checking event live status:', error);
          return false;
        }
      })
    );
  }

  protected readonly mapEventTypeToCategory = mapEventTypeToCategory;
}
